import * as React from 'react';
import { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

// External link to the current Rocket Learning site (rl-web).
// Centralised so we can swap to /start-trial later if conversion data
// shows it beats the marketing landing page.
const NEW_SITE_URL = "https://rocketlearning.uk";

const theme = createTheme({
  typography: {
    htmlFontSize: 20,
    h1: {
      fontSize: 'clamp(2rem, 5vw, 3rem)',
      fontWeight: 'bold',
    },
    h5: {
      fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
    },
    body1: {
      fontSize: '1.2rem',
    },
  },
});

/**
 * Legacy site homepage / "About".
 *
 * This used to be a full marketing landing page selling the legacy
 * product. The site is now sundowning — its job is to let existing
 * customers log in and bridge anyone else over to rocketlearning.uk
 * (the current platform). Marketing copy lives there now; we don't
 * try to compete with it here.
 *
 * Routed at /, /about, and /aboutus from App.js.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const [userType, setUserType] = useState(localStorage.getItem('usertype'));

  // Keep the dashboard CTA in sync if another tab logs in/out.
  useEffect(() => {
    const handleStorageChange = () => {
      setUserType(localStorage.getItem('usertype'));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const goToDashboard = () => {
    if (userType === 'parent') navigate('/ParentDashboard');
    else if (userType === 'student') navigate('/StudentDashboard');
  };

  return (
    <ThemeProvider theme={theme}>
      <Helmet>
        <title>Rocket Learning Legacy — Log In</title>
        <meta
          name="description"
          content="Existing Rocket Learning customers — sign in here. The latest version of Rocket Learning lives at rocketlearning.uk."
        />
      </Helmet>

      <CssBaseline />

      <Box sx={{ background: 'linear-gradient(135deg, #4a91e3, #4a91e3)', minHeight: '100vh' }}>

        {/* Top banner — slim, reassures legacy customers and points
            new/curious visitors at the current site. The bulk of the
            primary actions are below in the hero. */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box
            sx={{
              background: 'linear-gradient(90deg, #1A1640 0%, #2D2470 50%, #1A1640 100%)',
              color: '#fff',
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 2, sm: 3 },
              textAlign: 'center',
              borderBottom: '3px solid #FFCC00',
            }}
          >
            <Typography
              component="div"
              sx={{
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                color: 'rgba(255,255,255,0.92)',
                lineHeight: 1.4,
              }}
            >
              You&apos;re on the <strong style={{ color: '#FFCC00' }}>legacy</strong> Rocket Learning.
              The latest version is at{' '}
              <a
                href={NEW_SITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#FFCC00', fontWeight: 'bold', textDecoration: 'underline' }}
              >
                rocketlearning.uk
              </a>
              .
            </Typography>
          </Box>
        </motion.div>

        {/* Contact strip — kept thin; legacy customers occasionally need
            to reach us about billing and we don't want them hunting. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#f2f2f2',
            py: 1,
            px: 3,
          }}
        >
          <Button
            variant="contained"
            sx={{
              fontSize: '0.95rem',
              fontWeight: 'bold',
              textTransform: 'none',
              backgroundColor: '#007BFF',
              px: 2.5,
              py: 0.75,
              borderRadius: 1,
              '&:hover': { backgroundColor: '#0056b3' },
            }}
            onClick={() => navigate('/contactus')}
          >
            Contact Us
          </Button>
        </Box>

        {/* ─── HERO: Login first, new-site option second ──────────── */}
        <Container
          maxWidth="md"
          sx={{
            py: { xs: 6, sm: 10 },
            mt: { xs: 3, sm: 5 },
            mb: { xs: 3, sm: 5 },
            textAlign: 'center',
            background: 'linear-gradient(180deg, #4a91e3, #6db3f2)',
            borderRadius: 2,
            color: 'white',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Typography
              variant="h1"
              sx={{
                textShadow: '-2px -2px 0 #fff, 2px -2px 0 #fff, -2px 2px 0 #fff, 2px 2px 0 #fff',
              }}
            >
              Welcome back to <span style={{ color: '#f2182c' }}>Rocket Learning</span>
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mt: 3,
                color: '#fff',
                fontWeight: 500,
                maxWidth: 560,
                mx: 'auto',
              }}
            >
              Your account, lessons, and progress are right where you left them. Sign in to continue.
            </Typography>
          </motion.div>

          {/* Logged-in shortcut — if we already know who they are,
              skip the login chooser and surface a single dashboard CTA. */}
          {(userType === 'parent' || userType === 'student') ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <Button
                onClick={goToDashboard}
                variant="contained"
                size="large"
                sx={{
                  mt: 5,
                  backgroundColor: '#FFCC00',
                  color: '#000',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  px: 6,
                  py: 1.75,
                  borderRadius: 2,
                  fontSize: '1.15rem',
                  '&:hover': { backgroundColor: '#FFB700' },
                }}
              >
                Go to your dashboard →
              </Button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
            >
              <Box
                sx={{
                  mt: 5,
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  onClick={() => navigate('/ParentLogin')}
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#FFCC00',
                    color: '#000',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.05rem',
                    minWidth: 200,
                    '&:hover': { backgroundColor: '#FFB700' },
                  }}
                >
                  Parent log in
                </Button>
                <Button
                  onClick={() => navigate('/StudentLogin')}
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#FFCC00',
                    color: '#000',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontSize: '1.05rem',
                    minWidth: 200,
                    '&:hover': { backgroundColor: '#FFB700' },
                  }}
                >
                  Student log in
                </Button>
              </Box>
              <Typography
                sx={{
                  mt: 2.5,
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.95rem',
                }}
              >
                Forgotten your password?{' '}
                <a
                  href="/forgot-password"
                  style={{ color: '#FFCC00', fontWeight: 'bold' }}
                >
                  Reset it here
                </a>
                .
              </Typography>
            </motion.div>
          )}
        </Container>

        {/* ─── BRIDGE: try the new site ────────────────────────────── */}
        <Container
          maxWidth="md"
          sx={{
            mt: { xs: 4, sm: 6 },
            mb: { xs: 4, sm: 8 },
          }}
        >
          <Divider sx={{ mb: 4, borderColor: 'rgba(255,255,255,0.25)' }}>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.9rem',
                px: 2,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Or
            </Typography>
          </Divider>

          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 3,
              p: { xs: 3, sm: 5 },
              textAlign: 'center',
              backdropFilter: 'blur(6px)',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                mb: 1.5,
                fontSize: 'clamp(1.4rem, 3vw, 2rem)',
              }}
            >
              Try the new Rocket Learning
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.92)',
                mb: 3,
                fontSize: '1.05rem',
                maxWidth: 560,
                mx: 'auto',
              }}
            >
              8,000+ interactive lessons, KS1 to GCSE, native iPad app, weekly progress reports.
              14-day free trial — no card required.
            </Typography>
            <Button
              component="a"
              href={NEW_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              sx={{
                backgroundColor: '#FFCC00',
                color: '#000',
                fontWeight: 'bold',
                textTransform: 'none',
                px: 5,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.05rem',
                '&:hover': { backgroundColor: '#FFB700' },
              }}
            >
              Visit rocketlearning.uk →
            </Button>
          </Box>
        </Container>

        {/* ─── REASSURANCE FOOTER ──────────────────────────────────── */}
        <Container
          maxWidth="md"
          sx={{
            mb: 6,
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.78)',
              fontSize: '0.9rem',
              lineHeight: 1.5,
              maxWidth: 520,
              mx: 'auto',
            }}
          >
            This legacy site stays open as long as families are using it — nothing&apos;s changing if your learners prefer this version.
            Questions about your account? <a href="/contactus" style={{ color: '#FFCC00', fontWeight: 600 }}>Get in touch</a>.
          </Typography>
        </Container>

      </Box>
    </ThemeProvider>
  );
}
